import { useCallback, useState } from "react";

import { validate as isValidUUID } from "uuid";

import { ApiError } from "src/core/apiError";
import { Bilge } from "src/gen/rest/bilge/BilgeService";
import {
  GetCandidateInfoWithPasscodeRequest,
  GetCandidateInfoWithPasscodeResponse,
  LinkedAccountCandidate
} from "src/gen/schema/bilge/api";

interface UsePasscodeValidationReturn {
  passcode: string;
  passcodeError: string | undefined;
  error: ApiError | undefined;
  validating: boolean;
  candidateInfo: LinkedAccountCandidate | undefined;
  handlePasscodeChange: (e: React.FormEvent<HTMLInputElement>) => void;
  resetPasscode: () => void;
}

/**
 * Custom hook for passcode validation with proper cleanup
 */
export const usePasscodeValidation = (): UsePasscodeValidationReturn => {
  const [passcode, setPasscode] = useState<string>("");
  const [passcodeError, setPasscodeError] = useState<string | undefined>();
  const [error, setError] = useState<ApiError | undefined>();
  const [validating, setValidating] = useState<boolean>(false);
  const [candidateInfo, setCandidateInfo] = useState<
    LinkedAccountCandidate | undefined
  >();

  // Handle passcode input change
  const handlePasscodeChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const validatePasscode = (code: string) => {
        setValidating(true);
        const request = GetCandidateInfoWithPasscodeRequest.create({
          passcode: code
        });
        Bilge.getChatAccountCandidateWithPasscode(request)
          .then((response: GetCandidateInfoWithPasscodeResponse) => {
            setCandidateInfo(response.candidate);
          })
          .catch((e: ApiError) => {
            setError(e);
          })
          .finally(() => {
            setValidating(false);
          });
      };
      const code = e.currentTarget.value.trim();

      setPasscode(code);
      setValidating(false);
      setPasscodeError(undefined);
      setCandidateInfo(undefined);
      setError(undefined);

      if (!isValidUUID(code)) {
        setPasscodeError("Incorrect binding code");
        return;
      }

      // Valid UUID format - call validation API
      validatePasscode(code);
    },
    []
  );

  // Reset all state
  const resetPasscode = useCallback(() => {
    setPasscode("");
    setError(undefined);
    setPasscodeError(undefined);
    setValidating(false);
    setCandidateInfo(undefined);
  }, []);

  return {
    passcode,
    error,
    passcodeError,
    validating,
    candidateInfo,
    handlePasscodeChange,
    resetPasscode
  };
};
